import React, { useCallback, useMemo, useState } from 'react';
import { Image, StyleSheet, Text, View } from 'react-native';
import { ScreenProps } from '../../../typesInterfacesEnums/componentProps';
import { ComponentWrapper } from '../../helperComponents/ComponentWrapper';
import { Divider } from '../../helperComponents/Divider';
import { applyTransparency } from '../../../constants/colors';
import {
  GAME_LINK_TYPE,
  GAME_OPERATION_TYPE,
  GAME_TYPE_ARRANGEMENT,
  GAME_TYPES_CONFIG,
  GAME_TYPES_TYPE,
  PaymentStatus,
} from '../../../constants/constants';
import { GameTypeCard } from '../../helperComponents/GameTypeCard';
import { useSignedInContext } from '../../../context/SignedInContext';
import { useGetHivesQuery } from '../MyHives/useGetHivesQuery.gql';
import { DropdownMenu } from '../../helperComponents/DropdownMenu/DropdownMenu';
import { isMobile, isMobileApp } from '@mightybyte/rnw.utils.device-info';
import { PlusIconV2 } from '../../../resources/svgComponents/PlusIconV2';
import { XIconV2 } from '../../../resources/svgComponents/XIconV2';
import { GoPremiumPopup } from '../../helperComponents/GoPremiumPopup';
import { PremiumPlansPopup } from '../../helperComponents/PremiumPlansPopup';
import { MinusIconV2 } from '../../../resources/svgComponents/MinusIconV2';
import { DivideIconV2 } from '../../../resources/svgComponents/DivideIconV2';
import { HomeButton } from '../../helperComponents/HomeButton';
import { GameOperationTypeButton } from '../../helperComponents/GameOperationTypeButton';
import { PlusIconShadow } from '../../../resources/svgComponents/PlusIconShadow';
import { DivideIconShadow } from '../../../resources/svgComponents/DivideIconShadow';
import { MinusIconShadow } from '../../../resources/svgComponents/MinusIconShadow';
import { XIconShadow } from '../../../resources/svgComponents/XIconShadow';
import { BackButtonV2 } from '../../helperComponents/BackButtonV2';
import { USER_TYPES } from '../../../typesInterfacesEnums/enums';
import { useHiveUserQuery } from '../MyHiveData/useHiveUserQuery.gql';

const plusBackground = require('../../../resources/images/plusBackground.png');
const minusBackground = require('../../../resources/images/minusBackground.png');
const multiplyBackground = require('../../../resources/images/multiplyBackground.png');
const divideBackground = require('../../../resources/images/divideBackground.png');

const backgroundImages: Record<GAME_OPERATION_TYPE, any> = {
  [GAME_OPERATION_TYPE.ADDITION]: plusBackground,
  [GAME_OPERATION_TYPE.SUBTRACTION]: minusBackground,
  [GAME_OPERATION_TYPE.MULTIPLICATION]: multiplyBackground,
  [GAME_OPERATION_TYPE.DIVISION]: divideBackground,
};

const SelectGameType = ({ navigation, route }: ScreenProps<'SelectGameType'>) => {
  const { isSignedIn, user } = useSignedInContext();
  const { data: getHivesData, loading } = useGetHivesQuery({ skip: !isSignedIn });
  const [isGoPremiumPopupVisible, setIsGoPremiumPopupVisible] = useState(false);
  // const [isPremiumPlansPopupVisible, setPremiumPlansPopupVisible] = useState(false);
  const [selectedGameOperationType, setSelectedGameOperationType] =
    useState<GAME_OPERATION_TYPE | null>(null);

  const journey = route.params?.journey;
  const gymAssessment = route.params?.gymAssessment;
  const hiveId = route.params.hiveId;
  const hiveDetail = route.params.hiveDetail;
  const currentHive = getHivesData?.hives.find(hive => hive.id === hiveId);
  const userId = user?.id;

  //TODO: change to specific hive once hive selection feature is added
  // admin and teacher have access to all games
  const allowedGames = useMemo(() => {
    if (user?.userType === USER_TYPES.teacher || user?.userType === USER_TYPES.admin) {
      return [];
    }
    return (
      currentHive?.allowedHiveGames ||
      getHivesData?.hives.map(hive => hive.allowedHiveGames).flat() ||
      []
    );
  }, [currentHive?.allowedHiveGames, getHivesData?.hives, user?.userType]);

  const shouldSkip =
    !currentHive ||
    !user ||
    user?.userType === USER_TYPES.teacher ||
    user?.userType === USER_TYPES.admin;

  const {
    data: hiveUserData,
    loading: hiveUserLoading,
    error,
  } = useHiveUserQuery(
    { hiveId: currentHive?.id ?? '', userId: userId ?? '' },
    { skip: shouldSkip },
  );

  const allowedGamesFromHiveUser = hiveUserData?.hiveUser?.allowedHiveGames;

  const gameLinkType = useMemo(() => {
    if (gymAssessment) return GAME_LINK_TYPE.GYM_ASSESSMENT;
    if (journey) return GAME_LINK_TYPE.JOURNEY;
    return GAME_LINK_TYPE.AI;
  }, [gymAssessment, journey]);

  const GameOperationTypeIcon = useMemo(
    () => ({
      [GAME_OPERATION_TYPE.ADDITION]: <PlusIconShadow />,
      [GAME_OPERATION_TYPE.SUBTRACTION]: <MinusIconShadow />,
      [GAME_OPERATION_TYPE.MULTIPLICATION]: <XIconShadow />,
      [GAME_OPERATION_TYPE.DIVISION]: <DivideIconShadow />,
    }),
    [],
  );

  const nextRoute: 'PlayOnline' | 'HostGame' | 'PlayWithAI' | 'WaitingForHiveMate' | 'PassAndPlay' =
    route.params.next;
  const goToGame = useCallback(
    (gameType: GAME_TYPES_TYPE) => {
      if (nextRoute === 'WaitingForHiveMate') {
        if (
          route.params.inviteeId &&
          route.params.inviteeName &&
          route.params.userName &&
          route.params.hiveId
        ) {
          navigation.navigate(nextRoute, {
            gameType: gameType,
            userName: route.params.userName,
            inviteeUserId: route.params.inviteeId,
            inviteeUsername: route.params.inviteeName,
          });
        }
      } else if (nextRoute === 'PassAndPlay') {
        navigation.navigate('PassAndPlay', { gameType });
      } else {
        navigation.navigate(nextRoute, {
          gameType,
          userName: route.params.userName,
        });
      }
    },
    [navigation, nextRoute, route.params], // dependencies
  );

  const hasAccessToPremiumGames = useMemo(() => {
    const paidHives = getHivesData?.hives.filter(
      hive => hive.paymentStatus === PaymentStatus.Active,
    );
    return (paidHives ?? []).length > 0;
  }, [getHivesData?.hives]);

  const onSelectType = useCallback(
    (newSelectedType: GAME_TYPES_TYPE, options?: { isLocked?: boolean }) => {
      if (options?.isLocked && !hasAccessToPremiumGames) {
        setIsGoPremiumPopupVisible(true);
      }
      if (gameLinkType === GAME_LINK_TYPE.JOURNEY) {
        navigation.navigate('LobbyJourney', {
          gameType: newSelectedType,
          journey: true,
        });
        return;
      }

      if (gameLinkType === GAME_LINK_TYPE.GYM_ASSESSMENT) {
        const currentParams = route.params || {};
        navigation.navigate('GymAssessment', {
          ...currentParams,
          gameType: newSelectedType,
          gymAssessment: true,
        });
        return;
      }

      // Otherwise, proceed with normal game flow
      goToGame(newSelectedType);
    },
    [hasAccessToPremiumGames, journey, goToGame, navigation],
  );

  const onGoPremium = useCallback(
    (type: 'Learn More' | 'Create Hive') => {
      setIsGoPremiumPopupVisible(false);
      if (type === 'Create Hive') {
        navigation.navigate('SelectHiveSize');
      } else {
        navigation.navigate('Subscriptions');
      }
    },
    [navigation],
  );

  const renderOperandWebBackground = (operation: GAME_OPERATION_TYPE) => {
    if (isMobile) {
      return null;
    }
    return (
      <Image
        resizeMode="contain"
        style={styles.operandImage}
        source={backgroundImages[operation]}
      />
    );
  };

  return (
    <ComponentWrapper
      hasInset
      hasScroll={true}
      style={{ paddingHorizontal: 0 }}
      showOperand={true}
      operand={selectedGameOperationType ?? undefined}
    >
      <Divider empty height={isMobile ? 100 : 100} />
      <DropdownMenu
        backButton={
          !selectedGameOperationType ? (
            <HomeButton
              onPress={() => navigation.reset({ index: 0, routes: [{ name: 'Lobby' }] })}
            />
          ) : (
            <BackButtonV2 onPress={() => setSelectedGameOperationType(null)} />
          )
        }
        top={20}
      />
      <Text style={styles.title}>
        Select {journey ? 'Journey' : 'Game'} {isMobile && !selectedGameOperationType && 'Type'}
      </Text>

      {!isMobileApp || (!selectedGameOperationType && <Divider empty height={30} />)}

      {isMobile &&
        !selectedGameOperationType &&
        Object.values(GAME_OPERATION_TYPE).map((operation: GAME_OPERATION_TYPE) => (
          <GameOperationTypeButton
            key={operation}
            operation={operation}
            onPress={setSelectedGameOperationType}
            additionalIcon={GameOperationTypeIcon[operation]}
            containerStyle={styles.GameOperationTypeButton}
          />
        ))}

      {/* Show only if not mobile and has selectedGameOperationType */}
      {(!isMobile || selectedGameOperationType) && (
        <View style={{ flexDirection: isMobile ? 'column' : 'row', alignItems: 'flex-start' }}>
          <View style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
            {(!isMobile || selectedGameOperationType === GAME_OPERATION_TYPE.ADDITION) && (
              <View style={[styles.operationsContainer, isMobile && { marginHorizontal: 0 }]}>
                {renderOperandWebBackground(GAME_OPERATION_TYPE.ADDITION)}
                <Text style={styles.typeText}>Addition</Text>
                <Divider empty height={12} />

                {GAME_TYPE_ARRANGEMENT.filter(
                  type => GAME_TYPES_CONFIG[type].operation === GAME_OPERATION_TYPE.ADDITION,
                ).map(type => (
                  <GameTypeCard
                    title={GAME_TYPES_CONFIG[type].name}
                    disabled={
                      GAME_TYPES_CONFIG[type].name === '1-12' &&
                      allowedGames.length === 0 &&
                      allowedGamesFromHiveUser?.length === 0
                        ? journey
                          ? loading || !hasAccessToPremiumGames
                          : false
                        : loading ||
                          !hasAccessToPremiumGames ||
                          !(Array.isArray(allowedGamesFromHiveUser)
                            ? // ✅ If user-specific list exists:
                              // - empty [] → allow all
                              // - non-empty → allow only if included
                              allowedGamesFromHiveUser.length === 0 ||
                              allowedGamesFromHiveUser.includes(type)
                            : // 🧩 Otherwise fallback to hive-level rules
                              allowedGames.length === 0 || allowedGames.includes(type))
                    }
                    onPress={() => onSelectType(type)}
                    operandIcon={<PlusIconV2 />}
                    gameType={type}
                    gameLinkType={gameLinkType}
                  />
                ))}
              </View>
            )}
            {(!isMobile || selectedGameOperationType === GAME_OPERATION_TYPE.SUBTRACTION) && (
              <View style={[styles.operationsContainer, isMobile && { marginHorizontal: 0 }]}>
                {renderOperandWebBackground(GAME_OPERATION_TYPE.SUBTRACTION)}
                <Text style={styles.typeText}>Subtraction</Text>
                <Divider empty height={12} />

                {GAME_TYPE_ARRANGEMENT.filter(
                  type => GAME_TYPES_CONFIG[type].operation === GAME_OPERATION_TYPE.SUBTRACTION,
                ).map(type => (
                  <GameTypeCard
                    title={GAME_TYPES_CONFIG[type].name}
                    disabled={
                      loading ||
                      !hasAccessToPremiumGames ||
                      !(Array.isArray(allowedGamesFromHiveUser)
                        ? // ✅ If user-specific list exists:
                          // - empty [] → allow all
                          // - non-empty → allow only if included
                          allowedGamesFromHiveUser.length === 0 ||
                          allowedGamesFromHiveUser.includes(type)
                        : // 🧩 Otherwise fallback to hive-level rules
                          allowedGames.length === 0 || allowedGames.includes(type))
                    }
                    onPress={() => onSelectType(type)}
                    operandIcon={<MinusIconV2 />}
                    gameType={type}
                    gameLinkType={gameLinkType}
                  />
                ))}
              </View>
            )}
            {(!isMobile || selectedGameOperationType === GAME_OPERATION_TYPE.DIVISION) && (
              <View style={[styles.operationsContainer, isMobile && { marginHorizontal: 0 }]}>
                {renderOperandWebBackground(GAME_OPERATION_TYPE.DIVISION)}
                <Text style={styles.typeText}>Division</Text>
                <Divider empty height={12} />
                {GAME_TYPE_ARRANGEMENT.filter(
                  type => GAME_TYPES_CONFIG[type].operation === GAME_OPERATION_TYPE.DIVISION,
                ).map(type => (
                  <GameTypeCard
                    title={GAME_TYPES_CONFIG[type].name}
                    disabled={
                      loading ||
                      !hasAccessToPremiumGames ||
                      !(Array.isArray(allowedGamesFromHiveUser)
                        ? // ✅ If user-specific list exists:
                          // - empty [] → allow all
                          // - non-empty → allow only if included
                          allowedGamesFromHiveUser.length === 0 ||
                          allowedGamesFromHiveUser.includes(type)
                        : // 🧩 Otherwise fallback to hive-level rules
                          allowedGames.length === 0 || allowedGames.includes(type))
                    }
                    onPress={() => onSelectType(type)}
                    operandIcon={<DivideIconV2 />}
                    gameType={type}
                    gameLinkType={gameLinkType}
                  />
                ))}
              </View>
            )}
          </View>
          <View style={{ flexDirection: isMobile ? 'column' : 'row', alignItems: 'flex-start' }}>
            <View style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
              {(!isMobile || selectedGameOperationType === GAME_OPERATION_TYPE.MULTIPLICATION) && (
                <View style={[styles.operationsContainer, isMobile && { marginHorizontal: 0 }]}>
                  {renderOperandWebBackground(GAME_OPERATION_TYPE.MULTIPLICATION)}
                  <Text style={styles.typeText}>Multiplication</Text>
                  <Divider empty height={12} />
                  {GAME_TYPE_ARRANGEMENT.filter(
                    type =>
                      GAME_TYPES_CONFIG[type].operation === GAME_OPERATION_TYPE.MULTIPLICATION,
                  ).map(type => (
                    <GameTypeCard
                      title={GAME_TYPES_CONFIG[type].name}
                      disabled={
                        GAME_TYPES_CONFIG[type].name === '1-12' &&
                        allowedGames.length === 0 &&
                        allowedGamesFromHiveUser?.length === 0
                          ? journey
                            ? loading || !hasAccessToPremiumGames
                            : false
                          : loading ||
                            !hasAccessToPremiumGames ||
                            !(Array.isArray(allowedGamesFromHiveUser)
                              ? // ✅ If user-specific list exists:
                                // - empty [] → allow all
                                // - non-empty → allow only if included
                                allowedGamesFromHiveUser.length === 0 ||
                                allowedGamesFromHiveUser.includes(type)
                              : // 🧩 Otherwise fallback to hive-level rules
                                allowedGames.length === 0 || allowedGames.includes(type))
                      }
                      onPress={() => onSelectType(type)}
                      operandIcon={<XIconV2 />}
                      gameType={type}
                      gameLinkType={gameLinkType}
                    />
                  ))}
                </View>
              )}
            </View>
          </View>
        </View>
      )}
      <Divider empty height={48} />

      <Divider empty height={isMobile ? 170 : 240} />
      <GoPremiumPopup
        visible={isGoPremiumPopupVisible}
        setVisible={setIsGoPremiumPopupVisible}
        onButtonClicked={onGoPremium}
      />
      {/* <PremiumPlansPopup
        visible={isPremiumPlansPopupVisible}
        setVisible={setPremiumPlansPopupVisible}
      /> */}
    </ComponentWrapper>
  );
};

export { SelectGameType };

const styles = StyleSheet.create({
  hivesContainer: {
    minHeight: 300,
  },
  loginContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  loginText: {
    fontFamily: 'Secular One',
    marginLeft: 6,
    fontSize: 20,
    marginTop: 4,
    color: '#453B3B',
    textShadowColor: applyTransparency('#000000', 0.15),
    textShadowOffset: { width: 0, height: 3 },
    textShadowRadius: 2,
  },
  nameContainer: {
    borderRadius: 12,
    width: 180,
    height: 40,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: -20,
  },
  name: {
    fontFamily: 'Secular One',
    fontSize: 18,
    color: '#453B3B',
    textShadowColor: applyTransparency('#000000', 0.15),
    textShadowOffset: { width: 0, height: 3 },
    textShadowRadius: 2,
  },
  title: {
    fontFamily: 'Secular One',
    fontSize: 28,
    textShadowColor: applyTransparency('#000000', 0.15),
    textShadowOffset: { width: 0, height: 4 },
    textShadowRadius: 2,
    color: '#E87030',
  },
  typeText: {
    fontFamily: 'Secular One',
    fontSize: 22,
    textShadowColor: applyTransparency('#000000', 0.15),
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 2,
    color: '#121212',
    fontWeight: '400',
    marginHorizontal: 0,
    zIndex: 10,
  },
  operationsContainer: {
    marginHorizontal: 60,
    marginTop: 24,
    position: 'relative',
  },
  GameOperationTypeButton: {
    marginBottom: 11,
  },
  operandImage: {
    position: 'absolute',
    top: -40,
    left: -70,
    width: 150, // adjust size as needed
    height: 150,
  },
});
